import { useState, useEffect } from 'react';
import { ConfirmModal } from '@/src/modules/components/ui/ConfirmModal';
import { createCreditPack, editCreditPack } from '../services/credit-pack.service';
import type { AdminCreditPackRow, CreditPackUnit } from '../types/credit-pack.types';

const inputClass =
  'w-full px-3 py-2 rounded-lg bg-surface-container-lowest border border-border-subtle text-sm focus:outline-none focus:ring-2 focus:ring-electric-violet/30';
const labelClass = 'block text-[10px] uppercase tracking-widest font-bold text-on-surface-variant mb-1';

interface Props {
  open: boolean;
  mode: 'create' | 'edit';
  pack?: AdminCreditPackRow | null;
  onClose: () => void;
  onSuccess: () => void;
}

export function CreditPackForm({ open, mode, pack, onClose, onSuccess }: Props) {
  const [code, setCode] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [unit, setUnit] = useState<CreditPackUnit>('VALUE');
  const [rupees, setRupees] = useState<number>(5000);
  const [credits, setCredits] = useState<number>(7000);
  const [displayOrder, setDisplayOrder] = useState<number>(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (mode === 'edit' && pack) {
      setDisplayName(pack.displayName);
      setUnit(pack.unit);
      setDisplayOrder(pack.displayOrder || 0);
      setError(null);
    } else {
      setCode('');
      setDisplayName('');
      setRupees(999);
      setCredits(10);
      setDisplayOrder(0);
      setError(null);
    }
  }, [mode, pack, open]);

  const handleSubmit = async () => {
    try {
      setLoading(true);
      setError(null);

      if (mode === 'create') {
        if (!code.trim()) {
          setError('Code is required (e.g. PARTNER_BASIC)');
          return;
        }
        if (!displayName.trim()) {
          setError('Display name is required');
          return;
        }
        if (rupees <= 0) {
          setError('Price must be greater than zero');
          return;
        }
        if (credits <= 0) {
          setError('Credits must be greater than zero');
          return;
        }

        await createCreditPack({
          code: code.trim().toUpperCase(),
          displayName: displayName.trim(),
          amountPaise: Math.round(rupees * 100),
          credits,
          currency: 'INR',
          unit,
          displayOrder,
        });
      } else if (pack) {
        if (!displayName.trim()) {
          setError('Display name is required');
          return;
        }

        await editCreditPack(pack.id, {
          displayName: displayName.trim(),
          displayOrder,
        });
      }

      onSuccess();
      onClose();
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Operation failed');
    } finally {
      setLoading(false);
    }
  };

  // GST is not charged (BUG-13), so net per credit equals the price per credit. If GST is ever
  // enabled (docs/runbooks/enable-gst-after-registration.md) this must divide out the tax.
  const netPerCredit = mode === 'create' && credits > 0 ? rupees / credits : 0;
  const marginPercent = netPerCredit > 0 ? ((netPerCredit - 17.4) / netPerCredit) * 100 : 0;

  return (
    <ConfirmModal
      open={open}
      title={mode === 'create' ? 'Create Partner Credit Pack' : `Edit Metadata — ${pack?.displayName}`}
      description={
        mode === 'create'
          ? 'Add a new Partner Credit Pack product. Prices and credits are frozen per version.'
          : 'Update mutable pack metadata. To change price or credits, use Reprice instead.'
      }
      confirmLabel={mode === 'create' ? 'Create Pack' : 'Save Changes'}
      loading={loading}
      onClose={onClose}
      onConfirm={handleSubmit}
    >
      <div className="space-y-4 pt-2">
        {error && (
          <div className="p-3 text-xs rounded-lg bg-red-500/10 border border-red-500/20 text-red-500">
            {error}
          </div>
        )}

        {mode === 'create' && (
          <div>
            <label className={labelClass}>Code (Upper Snake Case)</label>
            <input
              type="text"
              placeholder="PARTNER_BASIC"
              value={code}
              onChange={(e) => setCode(e.target.value.toUpperCase())}
              className={inputClass}
            />
          </div>
        )}

        <div>
          <label className={labelClass}>Display Name</label>
          <input
            type="text"
            placeholder="Partner Basic Pack"
            value={displayName}
            onChange={(e) => setDisplayName(e.target.value)}
            className={inputClass}
          />
        </div>

        {mode === 'create' && (
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className={labelClass}>Price (₹)</label>
              <input
                type="number"
                min="1"
                value={rupees || ''}
                onChange={(e) => setRupees(Number(e.target.value))}
                className={inputClass}
              />
            </div>
            <div>
              <label className={labelClass}>Credits</label>
              <input
                type="number"
                min="1"
                value={credits || ''}
                onChange={(e) => setCredits(Number(e.target.value))}
                className={inputClass}
              />
            </div>
          </div>
        )}

        {mode === 'create' && (
          <div>
            <label className={labelClass}>Unit</label>
            <select value={unit} onChange={(e) => setUnit(e.target.value as CreditPackUnit)} className={inputClass}>
              <option value="VALUE">Wallet — 1 credit = ₹1 (current model)</option>
              <option value="PUBLISH">Legacy — 1 credit = 1 publish</option>
            </select>
          </div>
        )}

        {unit === 'VALUE' && (
          <div className="p-3 rounded-lg bg-surface-container-low border border-border-subtle text-xs flex justify-between text-on-surface-variant" data-testid="pack-form-discount">
            <span>Partner discount (pack ratio):</span>
            <span className="font-semibold text-on-surface">
              {credits > rupees ? (((credits - rupees) / credits) * 100).toFixed(1) : '0.0'}% off
            </span>
          </div>
        )}

        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className={labelClass}>Display Order</label>
            <input
              type="number"
              min="0"
              value={displayOrder}
              onChange={(e) => setDisplayOrder(Number(e.target.value))}
              className={inputClass}
            />
          </div>
        </div>

        {mode === 'create' && unit === 'PUBLISH' && (
          <div className="p-3 rounded-lg bg-surface-container-low border border-border-subtle text-xs space-y-1">
            <div className="flex justify-between text-on-surface-variant">
              <span>Gross / Credit:</span>
              <span className="font-semibold text-on-surface">₹{(rupees / credits || 0).toFixed(2)}</span>
            </div>
            <div className="flex justify-between text-on-surface-variant">
              <span>Net / Credit:</span>
              <span className="font-semibold text-on-surface">₹{netPerCredit.toFixed(2)}</span>
            </div>
            <div className="flex justify-between items-center text-on-surface-variant pt-1 border-t border-border-subtle">
              <span>Margin vs ₹17.4 cost:</span>
              <span
                className={`font-bold ${
                  marginPercent >= 75 ? 'text-emerald-500' : 'text-amber-500'
                }`}
              >
                {marginPercent.toFixed(1)}% {marginPercent < 75 && '(Target ≥ 75%)'}
              </span>
            </div>
          </div>
        )}
      </div>
    </ConfirmModal>
  );
}
